import { useState, useEffect, useCallback, useMemo } from "react";
import { useAuth, api } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Contact, FileDown, RefreshCw, Search, Mail, Phone, Users, Home,
  ClipboardList, HardHat, BedDouble, KeyRound, Wrench, UserCircle,
} from "lucide-react";
import { toast } from "sonner";

const SOURCE_META = {
  booking:     { Icon: BedDouble,     color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  lease:       { Icon: KeyRound,      color: "bg-indigo-50 text-indigo-700 border-indigo-200" },
  request:     { Icon: ClipboardList, color: "bg-sky-50 text-sky-700 border-sky-200" },
  staff:       { Icon: HardHat,       color: "bg-amber-50 text-amber-800 border-amber-200" },
  stakeholder: { Icon: Users,         color: "bg-purple-50 text-purple-700 border-purple-200" },
  user:        { Icon: UserCircle,    color: "bg-slate-50 text-slate-700 border-slate-200" },
  vendor:      { Icon: Wrench,        color: "bg-orange-50 text-orange-700 border-orange-200" },
};

export default function Contacts() {
  const { user } = useAuth();
  const [data, setData] = useState({ contacts: [], total: 0, counts: {}, source_labels: {} });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sourceFilter, setSourceFilter] = useState("__all__");
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/housing/contacts");
      setData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load contacts");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return data.contacts.filter((c) => {
      if (sourceFilter !== "__all__" && !c.sources.includes(sourceFilter)) return false;
      if (!q) return true;
      const blob = `${c.name} ${c.email} ${c.phone} ${(c.sources || []).join(" ")} ${
        Object.values(c.extra || {}).join(" ")
      }`.toLowerCase();
      return blob.includes(q);
    });
  }, [data.contacts, search, sourceFilter]);

  const downloadExport = async (format) => {
    setExporting(true);
    try {
      const params = new URLSearchParams({ format });
      if (sourceFilter !== "__all__") params.set("source", sourceFilter);
      const res = await api.get(`/housing/contacts/export?${params.toString()}`, { responseType: "blob" });
      const ext = format === "xlsx" ? "xlsx" : "csv";
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `contacts-${new Date().toISOString().slice(0,10)}.${ext}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${ext.toUpperCase()} download started`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Export failed");
    } finally { setExporting(false); }
  };

  const SourcePill = ({ src }) => {
    const meta = SOURCE_META[src] || SOURCE_META.user;
    const Icon = meta.Icon;
    const label = data.source_labels?.[src] || src;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-[11px] font-medium ${meta.color}`} data-testid={`contact-source-${src}`}>
        <Icon className="h-3 w-3" />
        {label}
      </span>
    );
  };

  return (
    <div className="space-y-6" data-testid="contacts-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold text-[#1B4332] flex items-center gap-2">
            <Contact className="h-6 w-6" /> Contact directory
          </h1>
          <p className="text-sm text-[#525C58] mt-1">
            Every visitor, tenant, helper, vendor, stakeholder and app user across {user?.organization_id ? "your organisation" : "all organisations"} — deduplicated by email.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load} data-testid="contacts-refresh-btn">
            <RefreshCw className="h-4 w-4 mr-1" /> Refresh
          </Button>
          <Button variant="outline" size="sm" disabled={exporting} onClick={() => downloadExport("csv")} data-testid="contacts-export-csv-btn">
            <FileDown className="h-4 w-4 mr-1" /> CSV
          </Button>
          <Button size="sm" disabled={exporting} onClick={() => downloadExport("xlsx")} data-testid="contacts-export-xlsx-btn" className="bg-[#1B4332] hover:bg-[#143526]">
            <FileDown className="h-4 w-4 mr-1" /> Excel
          </Button>
        </div>
      </div>

      {/* Source counts */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
        <button
          onClick={() => setSourceFilter("__all__")}
          className={`p-3 rounded-lg border text-left transition ${sourceFilter === "__all__" ? "bg-[#1B4332] text-white border-[#1B4332]" : "bg-white border-[#E8E8E6] hover:bg-[#F4F4F1]"}`}
          data-testid="contacts-filter-all"
        >
          <div className="text-2xl font-semibold">{data.total}</div>
          <div className="text-xs opacity-80">All contacts</div>
        </button>
        {Object.entries(data.source_labels || {}).map(([k, label]) => {
          const meta = SOURCE_META[k] || SOURCE_META.user;
          const Icon = meta.Icon;
          const count = data.counts?.[k] || 0;
          const active = sourceFilter === k;
          return (
            <button
              key={k}
              onClick={() => setSourceFilter(active ? "__all__" : k)}
              className={`p-3 rounded-lg border text-left transition ${active ? "bg-[#1B4332] text-white border-[#1B4332]" : "bg-white border-[#E8E8E6] hover:bg-[#F4F4F1]"}`}
              data-testid={`contacts-filter-${k}`}
            >
              <div className="flex items-center gap-1 text-xs opacity-70 mb-1">
                <Icon className="h-3 w-3" /> {label}
              </div>
              <div className="text-xl font-semibold">{count}</div>
            </button>
          );
        })}
      </div>

      {/* Search bar */}
      <Card>
        <CardContent className="p-4 flex flex-col md:flex-row gap-3 md:items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#9AA39E]" />
            <Input
              placeholder="Search by name, email, phone, source, role…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
              data-testid="contacts-search-input"
            />
          </div>
          <Select value={sourceFilter} onValueChange={setSourceFilter}>
            <SelectTrigger className="md:w-56" data-testid="contacts-source-select">
              <SelectValue placeholder="Filter by source" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All sources ({data.total})</SelectItem>
              {Object.entries(data.source_labels || {}).map(([k, label]) => (
                <SelectItem key={k} value={k}>{label} ({data.counts?.[k] || 0})</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="text-sm text-[#525C58] whitespace-nowrap" data-testid="contacts-result-count">
            Showing <span className="font-semibold text-[#1B4332]">{filtered.length}</span> of {data.total}
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Directory</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-10 text-center text-[#9AA39E]" data-testid="contacts-loading">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="p-10 text-center text-[#9AA39E]" data-testid="contacts-empty">
              No contacts match your filters.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>Sources</TableHead>
                    <TableHead>Notes</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((c) => {
                    const ex = c.extra || {};
                    const noteBits = [];
                    if (ex.role) noteBits.push(ex.role);
                    if (ex.department) noteBits.push(`${ex.department} dept`);
                    if (ex.booking_status) noteBits.push(`Booking: ${ex.booking_status}`);
                    if (ex.lease_status) noteBits.push(`Lease: ${ex.lease_status}`);
                    if (ex.id_number) noteBits.push(`ID: ${ex.id_number}`);
                    if (ex.last_task) noteBits.push(`Last job: ${ex.last_task}`);
                    return (
                      <TableRow key={c.email} data-testid={`contact-row-${c.email}`}>
                        <TableCell className="font-medium text-[#1B4332]">{c.name}</TableCell>
                        <TableCell>
                          <a href={`mailto:${c.email}`} className="text-[#1B4332] hover:underline inline-flex items-center gap-1">
                            <Mail className="h-3 w-3" /> {c.email}
                          </a>
                        </TableCell>
                        <TableCell>
                          {c.phone ? (
                            <a href={`tel:${c.phone}`} className="text-[#525C58] hover:underline inline-flex items-center gap-1">
                              <Phone className="h-3 w-3" /> {c.phone}
                            </a>
                          ) : <span className="text-[#9AA39E]">—</span>}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {c.sources.map((s) => <SourcePill key={s} src={s} />)}
                          </div>
                        </TableCell>
                        <TableCell className="text-xs text-[#525C58] max-w-xs">
                          {noteBits.length ? noteBits.join(" · ") : <span className="text-[#9AA39E]">—</span>}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
